import React from 'react';
import type { OfferDetailData } from '../../lib/engagementService';
import { boostRealOffer, boostOfferApplications } from '../../lib/engagementService';
import type { SeedCampaign } from '../../lib/seedService';
import BoostPanel from './BoostPanel';

interface Props {
  detail: OfferDetailData;
  campaigns: SeedCampaign[];
  onBoostComplete: (label: string, campaign: string, count: number) => void;
}

const OfferCard: React.FC<Props> = ({ detail, campaigns, onBoostComplete }) => {
  const { positionName, city, contractType, salaryMin, salaryMax, description, employerName, isSeed, seedTag, stats, applications } = detail;

  const salary = salaryMin && salaryMax
    ? `${salaryMin}€ – ${salaryMax}€`
    : salaryMin ? `${salaryMin}€+` : salaryMax ? `≤ ${salaryMax}€` : '';

  return (
    <div className="bg-white rounded-xl border-2 border-emerald-200 p-6 space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">{positionName}</h2>
          {isSeed && <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">SEED</span>}
        </div>
        <p className="text-sm text-gray-500">
          {[city, contractType, salary, employerName].filter(Boolean).join(' · ')}
        </p>
        {seedTag && <p className="text-xs text-gray-400">Campagne : {seedTag}</p>}
        {description && <p className="text-xs text-gray-500 mt-2 line-clamp-3">{description}</p>}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {([
          { label: 'Swipes reçus', value: stats.swipes, color: 'text-indigo-600 bg-indigo-50' },
          { label: 'Favoris reçus', value: stats.favorites, color: 'text-amber-600 bg-amber-50' },
          { label: 'Candidatures', value: stats.applications, color: 'text-green-600 bg-green-50' },
        ] as const).map((s) => (
          <div key={s.label} className={`rounded-lg p-3 ${s.color}`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Boost */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Booster cette offre</h3>
        <div className="space-y-2">
          <BoostPanel
            campaigns={campaigns}
            label="+ Swipes"
            onBoost={(c, n, s) =>
              boostRealOffer({ campaignTag: c, targetOfferId: detail.id, action: 'swipe', count: n, spreadDays: s })
            }
            onComplete={(c, n) => onBoostComplete(`Swipes ${positionName}`, c, n)}
          />
          <BoostPanel
            campaigns={campaigns}
            label="+ Favoris"
            onBoost={(c, n, s) =>
              boostRealOffer({ campaignTag: c, targetOfferId: detail.id, action: 'favorite', count: n, spreadDays: s })
            }
            onComplete={(c, n) => onBoostComplete(`Favoris ${positionName}`, c, n)}
          />
          <BoostPanel
            campaigns={campaigns}
            label="+ Candidatures"
            onBoost={(c, n, s) =>
              boostOfferApplications({ campaignTag: c, offerId: detail.id, count: n, spreadDays: s })
            }
            onComplete={(c, n) => onBoostComplete(`Candidatures ${positionName}`, c, n)}
          />
        </div>
      </div>

      {/* Applications list */}
      {applications.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Candidatures ({applications.length})</h3>
          <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
            {applications.map((a) => (
              <div key={a.id} className="px-3 py-2 flex items-center justify-between text-xs">
                <span className="font-medium text-gray-700">{a.candidateName}</span>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded ${
                    a.status === 'pending' ? 'bg-yellow-50 text-yellow-700' :
                    a.status === 'accepted' ? 'bg-green-50 text-green-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {a.status}
                  </span>
                  <span className="text-gray-400">{a.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferCard;
